import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { DataGrid, GridRow } from '@mui/x-data-grid';
import { COLORS } from '../../../utils/Application_Constants';
import { useInvoicingSelectionContext } from '../../Contexts/InvoicingSelectionContext';
import { HIERARCHY_BORDER_STYLE } from './styles';
import DataGridSortIcon from '../../../Shared/Components/DataGridSortIcon';
import useDebounce from '../../../utils/useDebounce';

const TABLE_BORDER_STYLE = `1px solid ${COLORS.LT_MIDNIGHT10}`;

const createRowWithRef = (setRefsUpdated, lastChildRef) => (props) => {
    const { rowId, isLastVisible } = props;
    const rowRef = useRef(null);

    useEffect(() => {
        if (rowRef.current) {
            setRefsUpdated((prev) => {
                const newRefs = [...prev];

                const refIndex = newRefs.findIndex((ref) => ref.rowId === rowId);

                if (refIndex === -1) {
                    newRefs.push({ rowId, ref: rowRef.current });
                } else {
                    newRefs[refIndex] = { rowId, ref: rowRef.current };
                }

                return newRefs;
            });
        }

        return () => {
            setRefsUpdated((prev) => prev.filter((ref) => ref.rowId !== rowId));
        };
    }, [rowId, setRefsUpdated]);

    return (
        <GridRow
            ref={(ref) => {
                rowRef.current = ref;
                if (isLastVisible) {
                    // eslint-disable-next-line no-param-reassign
                    lastChildRef.current = ref;
                }
            }}
            {...props}
        />
    );
};

const shouldShowCorrectionColumn = (rows, columns) => {
    // Check if isCorrection column exists
    const hasCorrectionColumn = columns.some((col) => col.field === 'isCorrection');
    if (!hasCorrectionColumn) return false;

    // Check if any row has isCorrection = true
    return rows.some((row) => row.isCorrection === true);
};

const CollapsibleDataGridWithConnectorLines = ({
    bookingId,
    title,
    itemId,
    itemType,
    columns,
    rows,
    hierarchyConnectorVerticalRef,
    lastChildRef,
    disableSelection = false,
    isExpanded = false,
    isSelectable = true,
    sx = {},
}) => {
    const { getSelectedSubItems, handleUpdateSubItemsSelection } = useInvoicingSelectionContext();
    const rowSelectionModel = useMemo(() => {
        const ids = getSelectedSubItems(bookingId, itemType, itemId);
        return { type: 'include', ids: new Set(ids) };
    }, [getSelectedSubItems, bookingId, itemId]);
    const handleRowSelectionModelChange = useCallback((newSelectionModel) => {
        handleUpdateSubItemsSelection(bookingId, itemType, itemId, [...newSelectionModel.ids]);
    }, []);

    const columnVisibilityModel = useMemo(
        () => ({
            isCorrection: shouldShowCorrectionColumn(rows, columns),
        }),
        [rows, columns]
    );

    const containerRef = useRef(null);
    const gridHeaderRowRef = useRef(null);
    const [gridRowsRefs, setGridRowsRefs] = useState([]); // [{ rowId: number, ref: ref }]
    const [gridRowConnectorLines, setGridRowConnectorLines] = useState([]);
    // Update connector lines when grid rows refs change
    const updateConnectorLines = useCallback(() => {
        if (!hierarchyConnectorVerticalRef?.current || !containerRef?.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const verticalConnectorRect = hierarchyConnectorVerticalRef.current.getBoundingClientRect();
        const lines = [];

        gridRowsRefs.forEach(({ rowId, ref }) => {
            if (ref) {
                const rowRect = ref.getBoundingClientRect();
                // Calculate positions relative to the DataGrid container
                const relativeTop = rowRect.top - containerRect.top;
                const left = verticalConnectorRect.left - rowRect.left;

                lines.push({
                    id: rowId,
                    left: left + 1,
                    top: relativeTop + rowRect.height / 2,
                    width: -left,
                });
            }
        });

        // Update header connector line
        if (gridHeaderRowRef?.current) {
            const headerRect = gridHeaderRowRef.current.getBoundingClientRect();
            const relativeTop = headerRect.top - containerRect.top;
            const left = verticalConnectorRect.left - headerRect.left;
            lines.push({
                id: 'header',
                left: left + 1,
                width: -left,
                top: relativeTop + headerRect.height / 2,
            });
        }

        setGridRowConnectorLines(lines);
    }, [hierarchyConnectorVerticalRef, gridRowsRefs, gridHeaderRowRef]);

    const debouncedUpdateConnectorLines = useDebounce(updateConnectorLines, 10);

    useEffect(() => {
        // Update on mount and window resize
        debouncedUpdateConnectorLines();

        const handleResize = () => debouncedUpdateConnectorLines();
        const resizeObserver = new ResizeObserver(handleResize);
        if (hierarchyConnectorVerticalRef?.current)
            resizeObserver.observe(hierarchyConnectorVerticalRef.current);
        if (containerRef?.current) resizeObserver.observe(containerRef.current);
        window.addEventListener('resize', handleResize);
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', handleResize);
        };
    }, [debouncedUpdateConnectorLines]);

    const RowWithRefComponent = useMemo(() => createRowWithRef(setGridRowsRefs, lastChildRef), []);

    const [isDataGridVisible, setIsDataGridVisible] = useState(false);
    // Render the data grid when the item is expanded and hide it when it is collapsed
    // The timeout is used to prevent the data grid from flashing when the item is collapsed
    useEffect(() => {
        let timeoutId;

        if (isExpanded) {
            setIsDataGridVisible(true);
        } else {
            timeoutId = setTimeout(() => {
                setIsDataGridVisible(false);
                setGridRowsRefs([]);
                setGridRowConnectorLines([]);
            }, 300);
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [isExpanded]);

    return (
        <Box position="relative" ref={containerRef}>
            {title && (
                <Typography sx={{ fontSize: 14, color: COLORS.LT_MIDNIGHT75 }} mb="18px">
                    {title}
                </Typography>
            )}

            {isDataGridVisible && (
                <DataGrid
                    rowSelectionModel={rowSelectionModel}
                    onRowSelectionModelChange={handleRowSelectionModelChange}
                    columnVisibilityModel={columnVisibilityModel}
                    sx={{
                        borderRadius: '5px',
                        border: TABLE_BORDER_STYLE,
                        background: COLORS.WHITE,
                        maxWidth: '800px',
                        '& .MuiDataGrid-iconSeparator': { display: 'none' },
                        '& .MuiDataGrid-columnHeaders': { marginBottom: '0px' },
                        '& .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader': {
                            borderBottom: 'none',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            '& .MuiCheckbox-root': { visibility: 'hidden' }, // hide the checkbox in the column header
                            backgroundColor: '#F4FAFF',
                        },
                        '& .MuiDataGrid-virtualScrollerContent': { borderTop: 'none' },
                        '& .MuiDataGrid-row': { '--DataGrid-rowBorderColor': COLORS.LT_MIDNIGHT10 },
                        '& .left-border': { borderLeft: TABLE_BORDER_STYLE },
                        '& .bold-text': { fontWeight: 'bold' },
                        '& .MuiDataGrid-footerContainer': {
                            display: rows?.length > 100 ? 'flex' : 'none',
                            '& .MuiTablePagination-displayedRows': { mb: '0px' },
                        },
                        ...sx,
                    }}
                    autoHeight
                    columnHeaderHeight={35}
                    rowHeight={48}
                    columns={columns}
                    rows={rows}
                    columnResizeIcon={false}
                    disableAutosize
                    disableColumnMenu
                    disableColumnResize
                    checkboxSelection={!disableSelection}
                    disableSelectionOnClick
                    disableRowSelectionOnClick
                    isRowSelectable={() => isSelectable}
                    slotProps={{
                        baseCheckbox: { tabIndex: 0 },
                        columnHeaders: { ref: gridHeaderRowRef },
                    }}
                    slots={{ columnHeaderSortIcon: DataGridSortIcon, row: RowWithRefComponent }}
                    pageSizeOptions={[100]}
                />
            )}

            {/* Horizontal connector lines */}
            {gridRowConnectorLines.map((line) => (
                <Box
                    key={line.id}
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        top: line.top,
                        left: line.left,
                        width: line.width,
                        height: 0,
                        borderTop: HIERARCHY_BORDER_STYLE,
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        zIndex: 1,
                    }}
                />
            ))}
        </Box>
    );
};

export default CollapsibleDataGridWithConnectorLines;
